import { useState } from 'react';
import {
  Plus,
  Upload,
  Download,
  Search,
  Loader2,
  Package,
  AlertTriangle,
} from 'lucide-react';
import { Card, CardContent, EmptyState, ErrorState } from '@mustauto/ui/web';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mustauto/ui/web';
import { useAuth } from '@/hooks/useAuth';
import {
  useCatalogList,
  useExportCatalogCsv,
  type CatalogListParams,
} from '@/hooks/useCatalog';
import { formatReais, formatNumber } from '@/lib/utils';
import { CatalogProductModal } from '@/components/CatalogProductModal';
import { CatalogProductDrawer } from '@/components/CatalogProductDrawer';
import { CatalogCsvImportModal } from '@/components/CatalogCsvImportModal';
import { CatalogLinkModal } from '@/components/CatalogLinkModal';

type Tab = 'all' | 'in_campaign' | 'no_campaign';
type Status = CatalogListParams['status'];

const CATEGORIES = [
  'Pneus', 'Freios', 'Suspensão', 'Motor', 'Transmissão', 'Elétrica',
  'Ar condicionado', 'Funilaria', 'Lubrificantes', 'Filtros', 'Baterias', 'Acessórios',
];

export function CatalogListPage() {
  const { user } = useAuth();
  const pid = user?.partnerId ?? '';

  const [tab, setTab] = useState<Tab>('all');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [statusFilter, setStatusFilter] = useState<Status>(undefined);
  const [page, setPage] = useState(1);

  // Map tab to status filter
  const effectiveStatus: Status =
    tab === 'no_campaign' ? 'no_campaign' :
    statusFilter;

  const { data, isLoading, isError, error, refetch } = useCatalogList(pid, {
    search: search || undefined,
    category: category || undefined,
    status: effectiveStatus,
    page,
    limit: 20,
  });

  const exportMutation = useExportCatalogCsv(pid);

  const [createOpen, setCreateOpen] = useState(false);
  const [editProductId, setEditProductId] = useState<string | null>(null);
  const [drawerProductId, setDrawerProductId] = useState<string | null>(null);
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [linkModalCampaignId, setLinkModalCampaignId] = useState<string | null>(null);

  const products = data?.products ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / 20);

  // For the "in_campaign" tab, filter client-side
  const displayProducts = tab === 'in_campaign'
    ? products.filter((p) => p.activeCampaignsCount > 0)
    : products;

  const noCampaignCount = tab === 'all'
    ? products.filter((p) => p.activeCampaignsCount === 0).length
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">Catálogo de produtos</h1>
          <Badge variant="outline">{total} produtos</Badge>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => exportMutation.mutate()}
            disabled={exportMutation.isPending}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50">
            <Download size={14} /> Exportar
          </button>
          <button onClick={() => setCsvImportOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-muted">
            <Upload size={14} /> Importar CSV
          </button>
          <button onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90">
            <Plus size={14} /> Novo produto
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Buscar por nome, SKU, marca..."
            className="w-full rounded-lg border border-border bg-muted pl-9 pr-4 py-2 text-sm outline-none focus:border-primary"
          />
        </div>

        <select
          value={category}
          onChange={(e) => { setCategory(e.target.value); setPage(1); }}
          className="rounded-lg border border-border bg-muted px-3 py-2 text-sm outline-none focus:border-primary"
        >
          <option value="">Todas categorias</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>

        <select
          value={statusFilter ?? ''}
          onChange={(e) => { setStatusFilter((e.target.value || undefined) as Status); setPage(1); }}
          className="rounded-lg border border-border bg-muted px-3 py-2 text-sm outline-none focus:border-primary"
        >
          <option value="">Todas as situações</option>
          <option value="active">Ativos</option>
          <option value="paused">Pausados</option>
          <option value="out_of_stock">Sem estoque</option>
        </select>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {([
          ['all', 'Todos'],
          ['in_campaign', 'Em campanha ativa'],
          ['no_campaign', 'Sem campanha'],
        ] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => { setTab(key); setPage(1); }}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 ${
              tab === key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* "No campaign" warning */}
      {tab === 'no_campaign' && displayProducts.length > 0 && (
        <Card className="border-amber-200 bg-amber-50 dark:border-amber-900/60 dark:bg-amber-950/30">
          <CardContent className="flex items-start gap-3 pt-4">
            <AlertTriangle size={18} className="mt-0.5 text-amber-600 dark:text-amber-400 shrink-0" />
            <div className="text-sm text-amber-800 dark:text-amber-300">
              <span className="font-medium">Atenção:</span>{' '}
              Estes {displayProducts.length} produtos não estão vinculados a nenhuma campanha ativa.
              Eles não aparecem para os clientes. Vincule-os a uma campanha para que sejam exibidos.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : isError && displayProducts.length === 0 ? (
        /* O hook devolve `data?.products ?? []`, entao a busca falhada chegava
           aqui como catalogo vazio — e a tela oferecia "adicione seu primeiro
           produto" a quem tem duzentos cadastrados. */
        <Card>
          <CardContent className="p-0">
            <ErrorState
              title="Não foi possível carregar seu catálogo"
              error={error}
              onRetry={() => void refetch()}
            />
          </CardContent>
        </Card>
      ) : displayProducts.length === 0 ? (
        <Card>
          <CardContent className="p-0">
            {/* Dois vazios diferentes na mesma condicao: "o filtro nao achou" e
                "o catalogo esta vazio" pedem acoes opostas, e so o segundo
                oferece cadastrar. */}
            {search || category || statusFilter || tab !== 'all' ? (
              /* A ABA e filtro tambem: em "Sem campanha" com o catalogo cheio,
                 esquecer dela fazia a tela afirmar "seu catalogo esta vazio". */
              <EmptyState
                icon={<Search size={30} />}
                title="Nenhum produto com esses filtros"
                description="Ajuste a busca, a categoria, o status ou a aba para ver mais resultados."
                action={{
                  label: 'Limpar filtros',
                  onClick: () => {
                    setSearch('');
                    setCategory('');
                    setStatusFilter(undefined);
                    setTab('all');
                    setPage(1);
                  },
                }}
              />
            ) : (
              <EmptyState
                icon={<Package size={30} />}
                title="Seu catálogo está vazio"
                description="Cadastre produtos aqui uma vez e vincule-os a quantas campanhas quiser."
                action={{ label: 'Adicionar primeiro produto', onClick: () => setCreateOpen(true) }}
              />
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">Foto</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead className="text-right">Preço</TableHead>
                  <TableHead>Campanhas</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayProducts.map((product) => (
                  <TableRow
                    key={product.id}
                    className="cursor-pointer"
                    onClick={() => setDrawerProductId(product.id)}
                  >
                    <TableCell>
                      {product.photoUrl ? (
                        <img src={product.photoUrl} alt={product.name}
                          className="h-10 w-10 rounded-md object-cover" />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-muted text-xs text-muted-foreground">—</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{product.name}</div>
                      {product.brand && (
                        <div className="text-xs text-muted-foreground">{product.brand}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{product.sku ?? '—'}</TableCell>
                    <TableCell>
                      {product.category ? <Badge variant="outline">{product.category}</Badge> : '—'}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {product.price ? formatReais(Number(product.price)) : '—'}
                    </TableCell>
                    <TableCell>
                      {product.activeCampaignsCount > 0 ? (
                        <Badge variant="info">{product.activeCampaignsCount}</Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">Nenhuma</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {product.outOfStock ? (
                        <Badge variant="warning">Sem estoque</Badge>
                      ) : product.active ? (
                        <Badge variant="success">Ativo</Badge>
                      ) : (
                        <Badge variant="secondary">Pausado</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="rounded-lg border border-border px-3 py-1.5 text-sm disabled:opacity-50"
          >
            Anterior
          </button>
          <span className="text-sm text-muted-foreground">
            Pagina {page} de {totalPages}
          </span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="rounded-lg border border-border px-3 py-1.5 text-sm disabled:opacity-50"
          >
            Proxima
          </button>
        </div>
      )}

      {/* Modals */}
      {createOpen && (
        <CatalogProductModal
          onClose={() => setCreateOpen(false)}
        />
      )}
      {editProductId && (
        <CatalogProductModal
          productId={editProductId}
          onClose={() => setEditProductId(null)}
        />
      )}
      {drawerProductId && (
        <CatalogProductDrawer
          productId={drawerProductId}
          onClose={() => setDrawerProductId(null)}
          onEdit={() => { setEditProductId(drawerProductId); setDrawerProductId(null); }}
          onLinkToCampaign={() => {
            // TODO: pick campaign first, then open link modal
            setDrawerProductId(null);
          }}
        />
      )}
      {csvImportOpen && (
        <CatalogCsvImportModal onClose={() => setCsvImportOpen(false)} />
      )}
    </div>
  );
}
